import React, {useState, useEffect} from 'react'
import useQuery from '@/hooks/useQuery'
import { Switch } from '@headlessui/react'
import { useForm } from 'react-hook-form'
import FroalaMinimalWrapper from '@/shared/FroalaMinimalWrapper'
import { ExclamationCircleIcon, EmojiHappyIcon, ExclamationIcon, LinkIcon, DuplicateIcon, ClipboardCopyIcon } from '@heroicons/react/solid'

import { PrimaryButton, DefaultButton } from '@/shared/Buttons'
import { Modal, useModal } from '@/shared/Modal'
import classNames from '@/utils/classNamesLocal'

import { showNonSpaToast } from '@/utils/nonSpaToast'
import { BulletItem, CaretItem } from '@/pages/teachers/Launch/Items'
import OutlineForm from '@/pages/teachers/LandingPage/OutlineForm'

const OutlineHeaderForm = ({course}) => {
  const {landing_page} = course
  const [landingPage, setLandingPage] = useState(landing_page)
  const [editing, setEditing] = useState(false)
  const [outlines, setOutlines] = useState([])
  const { putpostRequest, getRequest } = useQuery()
  const { formState, reset, register, unregister, handleSubmit, setValue, watch, getValues, setError } = useForm({
   defaultValues: {
      outline_header: landing_page.outline_header,
      outline_section: landing_page.outline_section === 'visible',
    }
  })
  const { isDirty, errors } = formState
  const [loading, setLoading] = useState(false)

  useEffect(() => fetchdata(), [])

  const handleUpdateSwitch = (key) => {
    setValue(key, !getValues()[key], { shouldDirty: true, shouldValidate: true })
  }

  const fetchdata = () => {
    getRequest(`/api/v1/teachers/courses/${course.id}/outlines`, {}, (err, jsonData) => {
      if (err) { /* handled in hook */ return }
      setOutlines(jsonData.outlines)
    })
  }

  const add = () => {
    setLoading(true)
    const data = {course_id: course.id}
    putpostRequest(`/api/v1/teachers/landing_page_outlines`, 'POST', data, (err, jsonData) => {
      setLoading(false)
      if (err) {
        showNonSpaToast(err, false)
        return
      }
      setOutlines([...outlines, jsonData.outline ])
    })
  }


  const onSubmit = (data) => {
    const formData = {
      landing_page: {
        outline_header: data.outline_header,
        outline_section: outline_section ? 'visible' : 'hidden'
      }
    }
    setLoading(true)
    putpostRequest(`/api/v1/teachers/courses/${course.id}/landing_page`, 'POST', formData, (err, jsonData) => {
      setLoading(false)
      if (err) {
        showNonSpaToast("Error", false)
        if (err.slug) {
          if (err.slug[0] === 'has already been taken') { setError('slug', { type: 'custom', message: 'unique' }) }
        }
        return
      }

      showNonSpaToast("Saved", true)
      setLandingPage({ ...landingPage, outline_header: jsonData.landing_page.outline_header })
      setEditing(false)
    })
  }

  const outline_section = watch('outline_section')

  return <>
    { !editing && <div id="outlines" className="hover:shadow-xl hover:opacity-50 py-2 cursor-pointer" onClick={() => setEditing(true)}>
      <div className="text-center mb-4">
        <h1 className="mt-1 text-2xl font-extrabold text-gray-900 sm:text-4xl sm:tracking-tight">{landingPage.outline_header}</h1>
        { !outline_section && <div>
          <div className="mx-auto flex justify-center">
            <div className="flex items-end self-start rounded-full border py-2 px-5 sm:self-center sm:py-3 bg-opacity-5 bg-red-100 border-red-200">
               <h6 className="b1 ml-2 text-red-800 dark:text-red-200 sm:ml-3">This section is hidden</h6>
            </div>
          </div>
        </div> }
        <div className="flex text-clcnavy justify-end">edit</div>
      </div>
      <div className='mx-auto max-w-xl'>
        { outlines.map((outl, idx) => (
        <div className="flex flex-row items-start border-b-2 border-dotted py-8 border-gray-500 last:border-b-0">
          <p className="prose-website-lg mr-4 text-gray-600">0{idx + 1}</p>
          <div>
            <p className="font-semibold text-gray-800">{outl.title}</p>
            <div className="py-2 text-gray-700"><span dangerouslySetInnerHTML={{ __html: outl.desc }}></span>
            </div>
          </div>
        </div>
        ))}
      </div>
    </div> }
    { editing && <form className='p-8 border-4 border-clcpurple rounded-lg'>
      <div className="p-8 pt-5">
        <div className="space-y-6">
          <div className='mt-3 grid grid-cols-3'>
            <Switch.Group as="div" className="col-span-1 flex items-center justify-between mb-2">
              <Switch.Label as="span" className="flex-grow flex flex-col" >
                <div className="prose-product-lg">
                  <h5 className="flex justify-between text-gray-800"><label className="mb-1">Show Section</label></h5>
                </div>
              </Switch.Label>
              <Switch checked={outline_section} onChange={() => handleUpdateSwitch('outline_section')}
                className={classNames(outline_section ? 'bg-clcpurple' : 'bg-gray-300 dark:bg-gray-500',
                  'relative inline-flex flex-shrink-0 h-6 w-11 border-2 border-transparent rounded-full cursor-pointer transition-colors ease-in-out duration-200 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-clcpurple')} >
                <span className="sr-only" hidden>Section is shown</span>
                <span aria-hidden="true" className={classNames(outline_section ? 'translate-x-5' : 'translate-x-0', 'pointer-events-none inline-block h-5 w-5 rounded-full bg-white shadow transform ring-0 transition ease-in-out duration-200')}
                />
              </Switch>
            </Switch.Group>
          </div>
          <div>
            <div className="prose-product-lg">
              <h5 className="flex justify-between text-gray-800"><label className="mb-1">Course Outline Section Header</label></h5>
            </div>
            <div className="prose-product-sm flex items-center sm:prose-product-lg">
              <input type="text" className={errors.outline_header ? 'errors placeholder-shown:italic placeholder-gray-600 p2 py-3.5 px-4 w-full border rounded-lg' : 'placeholder-shown:italic placeholder-gray-600 p2 py-3.5 px-4 w-full border rounded-lg'} {...register('outline_header', { required: true }) } defaultValue={landingPage.outline_header} placeholder="What People Are Saying" />
            </div>

            { errors.outline_header && <div className='mt-2 text-sm text-red-600'>This field is required.</div> }
          </div>
          <div className='flex gap-x-5'>
            <PrimaryButton onClick={handleSubmit(onSubmit)} loading={loading} text="Save" />
            <DefaultButton onClick={() => setEditing(false)} text="Cancel" />
          </div>
          <div>
            <div className="prose-product-sm flex justify-between sm:prose-product-lg">
              <div className="prose-product-lg">
                <h5 className="flex justify-between text-gray-800"><label className="mb-1">Outlines List</label></h5>
              </div>
            </div>
          </div>
          <ul className="list-disc space-y-2 flex flex-col">
            { outlines.map((outl, index) => (
              <OutlineForm key={`outlf${outl.id}`} outline={outl} refreshData={fetchdata} />
            ))}
          </ul>
        </div>
      </div>
      <div className='flex gap-x-5'>
        <PrimaryButton onClick={add} loading={loading} text="Add Outline Section" />
        <DefaultButton onClick={() => setEditing(false)} text="Done Editing" />
      </div>

    </form> }
  </>

}

export default OutlineHeaderForm
